<template>
    <div class="satellite">
        <v-container v-if="satellite">
            <v-layout row wrap>
                <!-- column 1 -->
                <v-flex pa-4 mt-2 xs12 sm6 class="grey--text text--darken-1">
                    <!-- description -->
                    <h1 class="heading">{{ satellite.displayName }}</h1>
                    <p v-html="satellite.longDescription"></p>
                    <ul>
                        <li><strong>Status:</strong> {{ satellite.status }}</li>
                        <li><strong>Launch Date:</strong> {{ formatLaunchDate(satellite.launchDate) }}</li>
                        <li v-if="satellite.lastPacketTime"><strong>Last packet :</strong>
                            {{ formatLaunchDate(satellite.lastPacketTime) }}</li>
                        <li><strong>Total confirmed satellite packets </strong>
                            <v-icon class="mr-1">
                                mdi-satellite-variant
                            </v-icon>: {{ satellite.confirmedPackets.toLocaleString() }}
                        </li>
                        <li><strong>Total stations received packets </strong>
                            <v-icon class="mr-1">
                                mdi-radio-tower
                            </v-icon>: {{ satellite.frames.toLocaleString() }}
                        </li>
                    </ul>
                    <v-card v-if="satellite.tle && satellite.tle.length == 3" flat
                        class="mr-5 my-3 pa-4 grey--text text--darken-3" style="overflow:auto;white-space: nowrap;">
                        <h3>TLEs</h3>
                        <v-card-text class="grey--text text--darken-3 mx-auto">
                            <pre>
{{ satellite.tle[0] }}
{{ satellite.tle[1] }}
{{ satellite.tle[2] }}
            </pre>
                        </v-card-text>
                    </v-card>
                    <!-- Telemetry -->
                    <v-card v-if="satellite.lastTelemetry && satellite.webTemplate" flat
                        class="mr-5 my-3 pa-4 grey--text text--darken-3">
                        <h2>Last telemetry</h2>
                        <v-card-text class="grey--text text--darken-3 mx-auto">
                            <DynamicTemplate :packet="lastTelemetry" :customTemplate="satellite.webTemplate" />
                        </v-card-text>
                    </v-card>
                </v-flex>
                <!-- Column 2 -->
                <v-flex pa-4 mt-2 xs12 sm6 class="grey--text text--darken-1">
                    <!-- satvis -->
                    <v-card flat class="mr-5 my-3 pa-2 grey--text text--darken-3" style="min-height:500px;">
                        <iframe id="satvis-frame" allowtransparency="true"
                            :src="`${viewerHost}/?elements=Point,Label,Orbit,Sensor-cone,Ground-station-link&layers=OfflineHighres&tags=TinyGS&sat=${satellite.displayName}`"
                            class="satvis"
                            :class="{ 'satvis-fullScreen': satvisFullScreen, 'satvis-normal': !satvisFullScreen }"></iframe>
                        <div class="credits-satvis">Powered by <a href="https://github.com/Flowm/satvis">Satvis</a>
                            (MIT)</div>
                        <v-btn @click="satvisFullScreen = !satvisFullScreen" class="mr-2"
                            :class="{ 'fullScreen-btn-fullScreen': satvisFullScreen, 'fullScreen-btn-normal': !satvisFullScreen }"
                            icon>
                            <v-img contain transition="fade-transition" max-height="60"
                                src="../assets/fullScren-but.png" />
                        </v-btn>
                    </v-card>
                    <!-- images -->
                    <v-layout row wrap>
                        <v-flex xs12 md6 v-for="image in satellite.images" :key="image">
                            <v-card class="ma-2">
                                <v-img :src="image" height="300px"></v-img>
                            </v-card>
                        </v-flex>
                    </v-layout>
                </v-flex>
            </v-layout>
            <!-- Packets -->
            <v-divider class="mt-6"></v-divider>
            <div class="pa-4" v-if="packets && packets.length > 0">
                <div v-for="packet in packets" :key="packet.id">
                    <PacketRow :packet="packet" :customTemplate="customTemplates[packet.satellite]" />
                    <v-divider></v-divider>
                </div>
            </div>
            <div class="pa-4" v-else>
                <div class="text-center grey--text text--darken-3">
                    No packets yet.
                </div>
            </div>
        </v-container>
        <div v-else> <!-- loading spinner -->
            <v-row class="fill-height ma-16" align="center" justify="center">
                <v-progress-circular indeterminate color="grey"></v-progress-circular>
            </v-row>
        </div>
    </div>
</template>

<script setup>
import { onBeforeMount, getCurrentInstance } from 'vue'
import PacketRow from '../components/PacketRow.vue'
import DynamicTemplate from '../components/DynamicTemplate.vue'
import { useSatellite } from '../composables/useSatellite.js'

// Obtener el nombre del satélite desde la instancia del componente (Vue 2.7)
const { proxy } = getCurrentInstance()
const satelliteName = proxy.$route.params.name

const {
    satellite,
    packets,
    customTemplates,
    satvisFullScreen,
    viewerHost,
    getSatellite,
    getPackets,
    formatLaunchDate,
    lastTelemetry
} = useSatellite(satelliteName)

onBeforeMount(() => {
    getSatellite()
    getPackets()
})
</script>

<style>
.clickable:hover {
    right: -1px;
}

.credits-satvis {
    position: absolute;
    right: 0;
    bottom: 0;
    margin: 9px;
    font-size: 0.7em;
    border-radius: 0 !important;
    background-color: rgba(255, 255, 255, 0.8);
    padding: 2px;
}

.satvis-fullScreen {
    position: fixed;
    top: 65px;
    left: 0;
    padding-bottom: 65px;
    height: 100%;
    width: 100%;
    z-index: 2;
}

.satvis-normal {
    display: block;
    width: 100%;
    height: 500px;
}

.fullScreen-btn-normal {
    position: absolute;
    right: 4px;
    bottom: 35px;
}

.fullScreen-btn-fullScreen {
    position: fixed;
    right: 20px;
    bottom: 20px;
    z-index: 3;
}
</style>